import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link } from "react-router-dom";

import { Container } from "components/misc/Layouts.js";
import { Eyebrow } from "blog/components/ui.js";
import allCategories from "blog/data/categories.js";
import { countByCategory } from "blog/data/posts";
import { getCategoryIcon } from "home/data/categoryIcons.js";

/**
 * Section « Explorer par catégorie » — bloc-carte léger placé sous « Derniers articles » :
 * les catégories du blog en pastilles, lues dans blog/data/categories.js (lecture seule).
 * On dit « catégorie » comme le blog (surtitre, navigation, URL /blog/categorie/), jamais
 * « thème » ni « rubrique ». Le cadre gris distingue les pastilles du bouton violet
 * « Voir tous les articles » qui le précède. Volontairement sans blob : respiration entre
 * deux sections décorées.
 *
 * Props (toutes optionnelles) :
 *  - subheading : libellé de la bande (défaut « Explorer par catégorie »)
 *  - categories : liste de catégories à afficher (défaut : toutes celles du blog)
 *  - showCounts : affiche le nombre d’articles par catégorie (défaut false —
 *    à activer quand chaque catégorie compte plusieurs articles)
 */

/* Pas de marge négative : le py-20/24 de la section précédente donne l’espace du haut,
   celui de la section suivante l’espace du bas ; on n’ajoute qu’un léger pb. */
const Strip = tw.div`max-w-screen-xl mx-auto pb-4 lg:pb-8 flex flex-col items-center`;
/* Retraits réduits en mobile (px-4) : le p-8 du wrapper de page s’y ajoute déjà. */
const Box = tw.div`w-full max-w-screen-lg mx-auto bg-gray-100 border-2 border-gray-200 rounded-lg px-4 sm:px-8 py-6 flex flex-col lg:flex-row items-center lg:justify-between`;
const StripLabel = tw(Eyebrow)`block mb-4 lg:mb-0 lg:mr-8 text-center lg:text-left flex-shrink-0`;
const Pills = tw.nav`flex flex-wrap justify-center lg:justify-end`;

const PillCount = tw.span`ml-2 text-xs font-semibold text-gray-700 transition duration-300`;

/* Pastille blanche à ombre légère (pas de seconde bordure grise dans la boîte) ; la bordure
   transparente garde la taille au survol, où elle devient violette. */
const Pill = styled(Link)(({ $color }) => [
  tw`inline-flex items-center m-1 sm:m-2 px-4 sm:px-5 py-3 rounded-full bg-white border-2 border-transparent shadow-sm text-sm font-bold text-secondary-500 transition duration-300 hocus:shadow-md hocus:bg-primary-500 hocus:border-primary-500 hocus:text-gray-100 focus:outline-none focus:shadow-outline`,
  css`
    svg {
      ${tw`w-4 h-4 mr-2 flex-shrink-0 transition duration-300`}
      color: ${$color};
    }
    &:hover svg,
    &:focus svg,
    &:hover ${PillCount},
    &:focus ${PillCount} {
      color: inherit;
    }
  `,
]);

export default function ExplorerParTheme({
  subheading = "Explorer par catégorie",
  categories = allCategories,
  showCounts = false,
}) {
  if (!categories || !categories.length) return null;
  const counts = showCounts ? countByCategory() : null;

  return (
    <Container id="categories">
      <Strip>
        <Box>
          <StripLabel id="categories-label">{subheading}</StripLabel>
          <Pills aria-labelledby="categories-label">
            {categories.map(c => {
              const Icon = getCategoryIcon(c.id);
              const count = counts ? counts[c.id] || 0 : null;
              const label =
                count !== null ? `${c.label} — ${count} article${count > 1 ? "s" : ""}` : undefined;
              return (
                <Pill
                  key={c.id}
                  to={`/blog/categorie/${c.slug}`}
                  $color={c.color}
                  title={c.description}
                  aria-label={label}
                >
                  <Icon aria-hidden="true" focusable="false" />
                  {c.label}
                  {count !== null && <PillCount aria-hidden="true">{count}</PillCount>}
                </Pill>
              );
            })}
          </Pills>
        </Box>
      </Strip>
    </Container>
  );
}
